import { Form, Formik } from 'formik';
import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useState } from 'react';
import { usePreventNavigation } from 'shared/hooks';
import UDFormInput from 'ud-form/components/input';
import UDFormLabel from 'ud-form/components/label';
import UDButton from 'ud-ui/components/button';
import Text from 'ud-ui/components/text';
import * as Yup from 'yup';
import { MigrationCredentials, MigrationService } from '../../services/migration-service';

const validationSchema = Yup.object().shape({
  sourceUrl: Yup.string().required('URL источника обязателен'),
  sourceKey: Yup.string().required('Ключ источника обязателен'),
  destinationUrl: Yup.string().required('URL назначения обязателен'),
  destinationKey: Yup.string().required('Ключ назначения обязателен'),
  tournamentId: Yup.mixed<number | null>()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) return null;
      const num = Number(originalValue);
      return Number.isNaN(num) ? null : num;
    })
    .test(
      'is-positive',
      'ID турнира должен быть положительным числом',
      (value) => value == null || (typeof value === 'number' && value > 0),
    ),
});

const initialValues: MigrationCredentials = {
  sourceUrl: '',
  sourceKey: '',
  destinationUrl: '',
  destinationKey: '',
  tournamentId: null,
};

const MigrationForm: React.FC = () => {
  const [migrationResult, setMigrationResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const handleSubmit = async (values: MigrationCredentials) => {
    setIsLoading(true);

    setMigrationResult(null);
    try {
      const migrationService = new MigrationService(values);
      const result = await migrationService.migrateData();
      setMigrationResult(result);
    } catch (error: Error | any) {
      setMigrationResult({
        success: false,
        message: `Ошибка: ${error.message}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const { PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.TEAMS.INDEX,
  });

  return (
    <div className="mt-4 w-full">
      <Text type="title-3" className="mb-4">
        Миграция данных
      </Text>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikHelpers) => {
          handleSubmit(values);
          formikHelpers.resetForm({ values }); // делаем текущие значения базой
          setIsFormDirty(false);
        }}
        validate={(values) => {
          const dirty = !_.isEqual(values, initialValues);
          if (dirty !== isFormDirty && !leavingConfirmed) {
            setIsFormDirty(dirty);
          }
          return {};
        }}
      >
        {({ isValid, dirty }) => (
          <Form className="flex flex-col gap-4">
            <div className="row">
              <div className="col-md-6">
                <Text type="headline" className="mb-2">
                  Источник (Source)
                </Text>
                <div className="mb-3">
                  <UDFormLabel htmlFor="sourceUrl">URL источника</UDFormLabel>
                  <UDFormInput name="sourceUrl" placeholder="https://example.supabase.co" />
                </div>
                <div className="mb-3">
                  <UDFormLabel htmlFor="sourceKey">Ключ источника</UDFormLabel>
                  <UDFormInput name="sourceKey" placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." />
                </div>
              </div>

              <div className="col-md-6">
                <Text type="headline" className="mb-2">
                  Назначение (Destination)
                </Text>
                <div className="mb-3">
                  <UDFormLabel htmlFor="destinationUrl">URL назначения</UDFormLabel>
                  <UDFormInput name="destinationUrl" placeholder="https://example.supabase.co" />
                </div>
                <div className="mb-3">
                  <UDFormLabel htmlFor="destinationKey">Ключ назначения</UDFormLabel>
                  <UDFormInput name="destinationKey" placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <UDFormLabel htmlFor="tournamentId">ID турнира (необязательно)</UDFormLabel>
                  <UDFormInput
                    name="tournamentId"
                    type="number"
                    placeholder="Введите ID турнира или оставьте пустым для стандартного шаблона"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-start">
              <UDButton
                type="submit"
                variant="primary"
                className="mr-4"
                disabled={!isValid || !dirty || isLoading}
                loading={isLoading}
              >
                Смигрировать данные
              </UDButton>
            </div>

            {migrationResult && (
              <div className={`alert ${migrationResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
                {migrationResult.message}
              </div>
            )}

            <PreventNavigationModal />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MigrationForm;
